/*
  React Flow Edge Converter

  This module handles converting document model edges to React Flow format.
*/

import { v4 as uuidv4 } from 'uuid';
import type { Edge as ReactFlowEdge } from 'react-flow-renderer';
import type { Edge as DocEdge } from '../../types';
import {
  DEFAULT_EDGE_STYLE,
  EDGE_LABEL_STYLE,
  EDGE_LABEL_BG_STYLE,
  WORKFLOW_OUTCOME_EDGE_STYLES,
} from './constants';

/**
 * Determine edge styling based on workflow outcome options
 *
 * @param config - The edge configuration
 * @returns Custom edge style or undefined
 */
function getWorkflowOutcomeEdgeStyle(
  config?: Record<string, any>,
): Record<string, any> | undefined {
  if (!config?.workflow_outcome_options) return undefined;

  const options = config.workflow_outcome_options as any;

  // Color edges based on workflow outcome options
  if (options.opensAlert) {
    return WORKFLOW_OUTCOME_EDGE_STYLES.alert;
  }

  if (options.opensInvestigation) {
    return WORKFLOW_OUTCOME_EDGE_STYLES.investigation;
  }

  return undefined;
}

/**
 * Convert a document edge to React Flow edge format
 *
 * This function creates a React Flow edge with:
 * - Proper source/target mapping
 * - Condition labels (if present)
 * - Workflow outcome-based styling
 * - Precedence data for ordering
 * - UUID generation for missing IDs
 *
 * @param docEdge - The document edge to convert
 * @param index - The index of the edge (unused, kept for compatibility)
 * @returns A React Flow edge
 */
export function convertDocEdgeToReactFlowEdge(
  docEdge: DocEdge,
): ReactFlowEdge {
  const baseEdge: ReactFlowEdge = {
    id: docEdge.id || uuidv4(),
    source: docEdge.from.node,
    target: docEdge.to.node,
    type: 'straight', // Use straight edges for precise routing
    animated: false,
    sourceHandle: 'source',
    targetHandle: 'target',
    style: { ...DEFAULT_EDGE_STYLE },
  };

  // Add condition label if present
  if (docEdge.condition) {
    baseEdge.label = docEdge.condition;
    baseEdge.labelStyle = { ...EDGE_LABEL_STYLE };
    baseEdge.labelBgStyle = { ...EDGE_LABEL_BG_STYLE };
  }

  // Apply workflow outcome-based styling
  const workflowOutcomeStyle = getWorkflowOutcomeEdgeStyle(docEdge.config);
  if (workflowOutcomeStyle) {
    baseEdge.style = {
      ...baseEdge.style,
      ...workflowOutcomeStyle,
    };
  }

  // Handle precedence for multiple edges from same source
  if (docEdge.precedence !== undefined) {
    baseEdge.data = {
      ...baseEdge.data,
      precedence: docEdge.precedence,
    };
  }

  return baseEdge;
}

